import { createContext, ReactElement, ReactNode, useContext } from 'react';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form';
import { useNavigate, useRouteLoaderData } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import * as z from 'zod';

import {
  billetSchema,
  BilletSchema,
} from '../components/HirePlanForm/Functionary-steps/data/schema/billetSchema';
import {
  funcionarioSchema,
  FuncionarioSchema,
} from '../components/HirePlanForm/Functionary-steps/data/schema/functionarysSchema';
import { useWebSocketAssinatura } from '../hooks/useWebSocketAssinatura';
import { useBoostedPlanDataStore } from '../stores/useBoostedPlanDataStore';
import { useError } from '../stores/useError';
import { useFunctionaryExistsStore } from '../stores/useFunctionaryStore';
import { useHirePlanFormStepStore } from '../stores/useHirePlanFormStepStore';
import { useLinkStore } from '../stores/useLinkStore';
import { useLoadingStore } from '../stores/useLoadingStore';
import {
  usePaymentStore,
  useValorTitularStore,
  useVigenciaStore,
} from '../stores/usePaymentStore';
import { useVidaCountStore } from '../stores/useQuantidadeVidas';
import { useResponseStore } from '../stores/useResponseStore';
import { useVerifyDataStore } from '../stores/verifiedStore';

type PaymentFormSchema = BilletSchema;
type funcionario = FuncionarioSchema;
interface HirePlanFormContextProps {
  register: UseFormRegister<PaymentFormSchema>;
  control: Control<PaymentFormSchema, any>;
  watch: UseFormWatch<PaymentFormSchema>;
  handleSubmit: UseFormHandleSubmit<PaymentFormSchema>;
  onSubmit: (schema: PaymentFormSchema) => void;
  goToNextForm: (schema: PaymentFormSchema) => void;
  errors: FieldErrors<PaymentFormSchema>;
  clearErrors: UseFormClearErrors<PaymentFormSchema>;
  unregister: UseFormUnregister<PaymentFormSchema>;
  setValue: UseFormSetValue<PaymentFormSchema>;
  funcionario: UseFormRegister<funcionario>;
  watchFunc: UseFormWatch<funcionario>;
  setFuncValue: UseFormSetValue<funcionario>;
  errorsFunc: FieldErrors<funcionario>;
}

const HirePlanFormContext = createContext({} as HirePlanFormContextProps);

interface HirePlanFormProviderProps {
  children: ReactNode;
}

export function IncludeFunctionaryProvider({
  children,
}: HirePlanFormProviderProps): ReactElement {
  const initialPlanData = useBoostedPlanDataStore(state => state.data);
  const goToNextStep = useHirePlanFormStepStore(state => state.goToNextStep);
  const paymentId = usePaymentStore(state => state.id);
  const navigate = useNavigate();
  const qtdVidas = useVidaCountStore(x => x.count);
  const dataEmpresa: any = useRouteLoaderData('Empresa-token');

  const funcionarioExistente = useFunctionaryExistsStore(x => x.existe);
  const plano = usePaymentStore(x => x.id);
  const ValorTitular = useValorTitularStore(x => x.valor);
  const vigencia = useVigenciaStore(x => x.vigencia);
  const setLoading = useLoadingStore(state => state.setLoading);
  const setError = useError(state => state.setError);
  const setTitle = useError(state => state.setErrorTitle);
  const setText = useError(state => state.setErrorText);
  const setResponse = useResponseStore(x => x.setResponse);
  const setTryed = useVerifyDataStore(state => state.setTryed);
  const setVerified = useVerifyDataStore(state => state.setVerifyData);
  const setLinkData = useLinkStore(state => state.setLinkData);
  const {
    register: funcionarioRegister,
    formState: { errors: funcionarioErrors },
    watch: funcionarioWatch,
    setValue: funcionarioSetValue,
  } = useForm<funcionario>({
    resolver: zodResolver(funcionarioSchema),
  });
  const { aguardarAssinatura } = useWebSocketAssinatura();

  const {
    register,
    handleSubmit,
    control,
    watch,
    unregister,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof billetSchema>>({
    resolver: zodResolver(billetSchema),
  });

  const goToNextForm: SubmitHandler<PaymentFormSchema> = () => {
    goToNextStep();
  };

  function formatDateToYYYYMM(dateStr: string): number {
    const [day, month, year] = dateStr.split('/');
    if (!day || !month || !year) {
      throw new Error('Data inválida. Formato esperado: dd/MM/yyyy');
    }
    return Number(`${year}${month.padStart(2, '0')}`);
  }
  const formatDate = (date: string) => {
    const [day, month, year] = date.split('/');
    return `${year}-${month}-${day}`;
  };

  const executarInclusao = async (
    data: PaymentFormSchema,
    MonthYear: number,
  ) => {
    try {
      const token = process.env.REACT_APP_TOKEN_S4E_V2;
      const dependentesComTitular = [
        {
          tipo: 1,
          nome: data.user.funcionario.nome,
          cpf: data.user.funcionario.cpf?.replace(/[.-]/g, ''),
          sexo: data.user.funcionario.sexo == '1' ? 0 : 1,
          plano: plano,
          planoValor: String(ValorTitular).replace(',', '.'),
          numeroProposta: '',
          carenciaAtendimento: 0,
          rcaId: 0,
          cns: data.user.funcionario.cns || '',
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          nomeMae: data.user.funcionario.motherName,
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(data.user.funcionario.dtNascimento),
          funcionarioCadastro: 62181,
          dataCadastroLoteContrato: '',
        },
        ...(data.newDependents?.map(dependent => ({
          tipo:
            dependent.parentesco == '10' || dependent.parentesco == '3'
              ? 3
              : dependent.parentesco == '11' || dependent.parentesco == '4'
              ? 4
              : 10,
          nome: dependent.nome,
          cpf: dependent.cpf?.replace(/[.-]/g, ''),
          sexo: dependent.sexo == '1' ? 0 : 1,
          plano: plano,
          planoValor: String(ValorTitular).replace(',', '.'),
          cns: dependent.cns || '',
          numeroProposta: '',
          carenciaAtendimento: 0,
          rcaId: 0,
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          nomeMae: dependent.motherName,
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(dependent.dtNascimento),
          funcionarioCadastro: 62181, // funcionario Ti
          dataCadastroLoteContrato: '',
        })) || []),
      ];
      const orgaoExp = initialPlanData.listaOrgaosExpedidor.find(
        x => x.id_oe === data.user.funcionario.orgao,
      );

      const checkoutData = {
        token: token,
        dados: {
          parcelaRetidaComissao: '0',
          incluirMensalidades: '1',
          parceiro: {
            codigo: data.user.codVendedor,
            tipoCobranca: 1,
            adesionista: 0,
            maxMensalidadeId: '1',
          },
          responsavelFinanceiro: {
            codigoContrato: data.user.codEmpresa,
            nome: data.user.funcionario.nome,
            dataNascimento: formatDate(data.user.funcionario.dtNascimento),
            cpf: data.user.funcionario.cpf?.replace(/[.-]/g, ''),
            sexo: data.user.funcionario.sexo == '1' ? 0 : 1,
            identidadeNumero: data.user.funcionario.rg,
            identidadeOrgaoExpeditor: orgaoExp?.sigla || '',
            enderecoBoleto: '',
            cd_orientacao_sexual: 0,
            OutraOrientacaoSexual: '',
            cd_ident_genero: 0,
            OutraIdentidadeGenero: '',
            agencia: 0,
            agencia_dv: 0,
            conta: 0,
            conta_dv: 0,
            contaOperacao: 0,
            matricula: data.user.funcionario.matricula,
            dataApresentacao: '',
            diaVencimento: '01',
            tipoPagamento: 529,
            origemVenda: 13,
            observacaoAssociado: '',
            codSistemaExterno: '',
            dataAssinaturaContrato: formatDate(vigencia),
            numeroProposta: String(Math.floor(Math.random() * 1000000000)),
            senhaAssociado: '',
            endereco: {
              cep: data.user.funcionario.address.cep,
              tipoLogradouro: data.user.funcionario.address.tipoLogradouro,
              logradouro: data.user.funcionario.address.street,
              numero: data.user.funcionario.address.number,
              complemento: data.user.funcionario.address.complement || '',
              bairro: data.user.funcionario.address.bairro,
              municipio: data.user.funcionario.address.municipio,
              uf: data.user.funcionario.address.ufId,
              descricaoUf: data.user.funcionario.address.ufDescription,
            },
            fl_AlteraSituacao: 16,
            contatoResponsavelFinanceiro: [
              {
                tipo: 8,
                dado: data.user.funcionario.Celular,
              },
              {
                tipo: 50,
                dado: data.user.funcionario.email,
              },
            ],
          },
          dependente: dependentesComTitular,
        },
        empresa: '',
      };
      const Url = `${process.env.REACT_APP_OG_API_URL}/include/ByPassPj`;
      await axios
        .post(Url, checkoutData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then(response => {
          setLoading(false);
          setError(false);
          return response.data;
        })
        .catch(error => {
          setLoading(false);
          setError(true);

          if (error.response) {
            if (error.response.data.code) {
              setTitle(error.response.data.code);
              setText(error.response.data.error);
              alert('Erro: ' + error.response.data.error);
              throw new Error(error.response.data.error);
            } else {
              setText(error.response.data.message);
              alert('Erro: ' + error.response.data.message);
              throw new Error(error.response.data.message);
            }
          } else {
            console.error('Erro de rede ou timeout:', error.message);
            setText('Erro na conexão com o servidor.');
            throw new Error('Erro na conexão com o servidor.');
          }
        })
        .then(() => {
          alert('Cadastro realizado com sucesso!');
        });
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const gerarDadosCheckout = async (
    data: PaymentFormSchema,
    MonthYear: number,
  ) => {
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    const dependentesComTitular = [
      {
        tipo: 1,
        nome: data.user.funcionario.nome,
        cpf: data.user.funcionario.cpf?.replace(/[.-]/g, ''),
        sexo: data.user.funcionario.sexo == '1' ? 0 : 1,
        plano: plano,
        planoValor: String(ValorTitular).replace(',', '.'),
        numeroProposta: '',
        carenciaAtendimento: 0,
        rcaId: 0,
        cns: data.user.funcionario.cns || '',
        cd_orientacao_sexual: 0,
        OutraOrientacaoSexual: '',
        cd_ident_genero: 0,
        OutraIdentidadeGenero: '',
        idExterno: '',
        nomeMae: data.user.funcionario.motherName,
        MMYYYY1Pagamento: MonthYear,
        numeroCarteira: '',
        observacaoUsuario: '',
        dataNascimento: formatDate(data.user.funcionario.dtNascimento),
        funcionarioCadastro: 62181,
        dataCadastroLoteContrato: '',
      },
      ...(data.newDependents?.map(dependent => ({
        tipo:
          dependent.parentesco == '10' || dependent.parentesco == '3'
            ? 3
            : dependent.parentesco == '11' || dependent.parentesco == '4'
            ? 4
            : 10,
        nome: dependent.nome,
        cpf: dependent.cpf?.replace(/[.-]/g, ''),
        sexo: dependent.sexo == '1' ? 0 : 1,
        plano: plano,
        planoValor: String(ValorTitular).replace(',', '.'),
        cns: dependent.cns || '',
        numeroProposta: '',
        carenciaAtendimento: 0,
        rcaId: 0,
        cd_orientacao_sexual: 0,
        OutraOrientacaoSexual: '',
        cd_ident_genero: 0,
        OutraIdentidadeGenero: '',
        idExterno: '',
        nomeMae: dependent.motherName,
        MMYYYY1Pagamento: MonthYear,
        numeroCarteira: '',
        observacaoUsuario: '',
        dataNascimento: formatDate(dependent.dtNascimento),
        funcionarioCadastro: 62181, // funcionario Ti
        dataCadastroLoteContrato: '',
      })) || []),
    ];
    const orgaoExp = initialPlanData.listaOrgaosExpedidor.find(
      x => x.id_oe === data.user.funcionario.orgao,
    );

    const checkoutData = {
      token: token,
      dados: {
        parcelaRetidaComissao: '0',
        incluirMensalidades: '1',
        parceiro: {
          codigo: data.user.codVendedor,
          tipoCobranca: 1,
          adesionista: 0,
          maxMensalidadeId: '1',
        },
        responsavelFinanceiro: {
          codigoContrato: data.user.codEmpresa,
          nome: data.user.funcionario.nome,
          dataNascimento: formatDate(data.user.funcionario.dtNascimento),
          cpf: data.user.funcionario.cpf?.replace(/[.-]/g, ''),
          sexo: data.user.funcionario.sexo == '1' ? 0 : 1,
          identidadeNumero: data.user.funcionario.rg,
          identidadeOrgaoExpeditor: orgaoExp?.sigla || '',
          enderecoBoleto: '',
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          agencia: 0,
          agencia_dv: 0,
          conta: 0,
          conta_dv: 0,
          contaOperacao: 0,
          matricula: data.user.funcionario.matricula,
          dataApresentacao: '',
          diaVencimento: '01',
          tipoPagamento: 529,
          origemVenda: 13,
          observacaoAssociado: '',
          codSistemaExterno: '',
          dataAssinaturaContrato: formatDate(vigencia),
          numeroProposta: String(Math.floor(Math.random() * 1000000000)),
          senhaAssociado: '',
          endereco: {
            cep: data.user.funcionario.address.cep,
            tipoLogradouro: data.user.funcionario.address.tipoLogradouro,
            logradouro: data.user.funcionario.address.street,
            numero: data.user.funcionario.address.number,
            complemento: data.user.funcionario.address.complement || '',
            bairro: data.user.funcionario.address.bairro,
            municipio: data.user.funcionario.address.municipio,
            uf: data.user.funcionario.address.ufId,
            descricaoUf: data.user.funcionario.address.ufDescription,
          },
          fl_AlteraSituacao: 16,
          contatoResponsavelFinanceiro: [
            {
              tipo: 8,
              dado: data.user.funcionario.Celular,
            },
            {
              tipo: 50,
              dado: data.user.funcionario.email,
            },
          ],
        },
        dependente: dependentesComTitular,
      },
      empresa: '',
    };
    return checkoutData;
  };

  const onSubmit: SubmitHandler<PaymentFormSchema> = async data => {
    const token = process.env.REACT_APP_TOKEN_S4E_V2;

    if (vigencia) {
      setValue('user.dtInicioVigencia', vigencia);
    }
    const MonthYear = formatDateToYYYYMM(vigencia);
    const cpfTitular = (
      data?.user?.funcionario?.cpf || data.cadastrado.cpf
    ).replace(/[.-]/g, '');
    if (!cpfTitular) {
      return;
    }
    if (data?.user?.codEmpresa == 27481 && !funcionarioExistente) {
      const validateResponse = await axios.post(
        `${process.env.REACT_APP_OG_API_URL}/SinpolPE/validate-cpf-simpol`,
        {
          cpf: cpfTitular,
          dadosFormulario: {
            nome: data?.user?.funcionario?.nome,
            email: data?.user?.funcionario?.email,
            tel1: data?.user?.funcionario?.Celular,
            tel2: data?.user?.funcionario?.telefoneRes,
          },
        },
      );
      if (
        (validateResponse && validateResponse.status !== 200) ||
        !validateResponse.data?.sindicalizado
      ) {
        setTryed(true);
        setVerified(false);
        handleSubmit(goToNextForm)();
        return;
      }
    }
    if (!funcionarioExistente) {
      setLoading(true);
      const checkoutData = await gerarDadosCheckout(data, MonthYear);
      const flowId = uuid();
      const dados = {
        WsId: flowId,
        cpf: cpfTitular,
        nome: data.user.funcionario.nome,
        email: data.user.funcionario.email,
        telefone: data.user.funcionario.Celular,
        cnpj: dataEmpresa?.cnpj,
        razaoSocial: dataEmpresa?.razao_social,
        dados: checkoutData,
      };
      try {
        const Assinatura = await axios.post(
          `${process.env.REACT_APP_OG_API_URL}/generate/AssinaturaFuncionario`,
          dados,
        );
        if (Assinatura.data.LinkAssinatura) {
          setLinkData(Assinatura.data.LinkAssinatura);
          handleSubmit(goToNextForm)();

          await aguardarAssinatura({
            flowId,
            onConfirm: async () => {
              //await executarInclusao(data, MonthYear);

              setLinkData(null);
              setTryed(true);
              setVerified(true);
            },
          });
          return;
        } else {
          alert('Erro ao gerar link de assinatura. Tente novamente.');
          setLoading(false);
          return;
        }
      } catch (error) {
        alert('Erro ao processar assinatura. Tente novamente.');
        setLoading(false);
        return;
      }
    } else {
      setLoading(true);
      if (data.newDependents?.length == 0) {
        alert('Nenhum dependente para incluir!');
        setLoading(false);
        return;
      }
      let newData: any = {};
      try {
        newData = {
          token: token,
          dados: {
            parceiro: {
              codigo: data.user.codVendedor,
              adesionista: 0,
            },
            responsavelFinanceiro: {
              codigo: data.inicial.idFuncionario,
              dataAssinaturaContrato: formatDate(vigencia),
            },
            dependente: data.newDependents?.map(dependent => ({
              tipo:
                dependent.parentesco == '10' || dependent.parentesco == '3'
                  ? 3
                  : dependent.parentesco == '11' || dependent.parentesco == '4'
                  ? 4
                  : 10,
              nome: dependent.nome,
              cpf: dependent.cpf?.replace(/[.-]/g, ''),
              sexo: dependent.sexo == '1' ? 0 : 1,
              plano: plano,
              planoValor: String(ValorTitular).replace(',', '.'),
              numeroProposta: '',
              carenciaAtendimento: 0,
              rcaId: 0,
              cd_orientacao_sexual: 0,
              OutraOrientacaoSexual: '',
              cd_ident_genero: 0,
              OutraIdentidadeGenero: '',
              idExterno: '',
              nomeMae: dependent.motherName,
              MMYYYY1Pagamento: String(MonthYear),
              numeroCarteira: '',
              observacaoUsuario: '',
              dataNascimento: formatDate(dependent.dtNascimento),
              funcionarioCadastro: 0,
              dataCadastroLoteContrato: '',
            })),
            contatoDependente: [],
          },
        };
      } catch (error) {
        //console.log('Erro ao formatar data:', error);
      }
      await fetch(`${process.env.REACT_APP_OG_POST_INCLUSAO_DEPENDENTE}es`, {
        headers: {
          'content-type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(newData),
      })
        .then(async response => {
          if (response.ok) {
            const responseData = await response.json();
            setLoading(false);
            setError(false);
            setResponse(responseData);
            alert('Dependentes incluídos com sucesso!');
            handleSubmit(goToNextForm)();
          }
          const errorData = await response.json();

          if (errorData.code) {
            setTitle(errorData.code);
            setText(errorData.error);
            alert('Erro: ' + errorData.error);
            setLoading(false);
            throw new Error(errorData.error);
          } else {
            setText(errorData.message);
            alert('Erro: ' + errorData.message);
            setLoading(false);
            throw new Error(errorData.message);
          }
        })
        .catch(error => {
          setLoading(false);
        });
    }
  };

  return (
    <HirePlanFormContext.Provider
      value={{
        register,
        control,
        handleSubmit,
        watch,
        onSubmit,
        goToNextForm,
        errors,
        clearErrors,
        unregister,
        setValue,
        funcionario: funcionarioRegister,
        watchFunc: funcionarioWatch,
        setFuncValue: funcionarioSetValue,
        errorsFunc: funcionarioErrors,
      }}
    >
      {children}
    </HirePlanFormContext.Provider>
  );
}

export function useIncludeFunctionaryForm(): HirePlanFormContextProps {
  return useContext(HirePlanFormContext);
}
